import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtMoney, studentLabel } from "@/lib/helpers";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, HandCoins, Buildings, HandHeart, User, ChartLine } from "@phosphor-icons/react";

const SOURCE_META = {
  gov:       { label: "Government scheme", icon: Buildings, color: "bg-blue-100 text-blue-800 border-blue-200" },
  csr:       { label: "Corporate CSR",      icon: ChartLine, color: "bg-purple-100 text-purple-800 border-purple-200" },
  foundation:{ label: "Foundation grant",   icon: HandCoins, color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  individual:{ label: "Individual donor",   icon: HandHeart, color: "bg-amber-100 text-amber-800 border-amber-200" },
};

export default function GrantsPage() {
  const [grants, setGrants] = useState([]);
  const [students, setStudents] = useState([]);
  const [therapies, setTherapies] = useState([]);
  const [show, setShow] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const emptyForm = {
    name: "", donor: "", source_type: "gov", amount: 0,
    allocation_mode: "pool", covered_student_ids: [], covered_therapy_type_ids: [],
    start_date: "", end_date: "", reference_no: "", document_url: "", notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [allocForm, setAllocForm] = useState({ student_id: "", amount: 0, note: "" });

  const load = async () => {
    try {
      const [g, s, t] = await Promise.all([
        api.get("/grants"), api.get("/students"), api.get("/therapy-types"),
      ]);
      setGrants(g.data || []);
      setStudents(s.data || []);
      setTherapies(t.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    setOpenId(id);
    try {
      const { data } = await api.get(`/grants/${id}`);
      setDetail(data);
      setAllocForm({ student_id: "", amount: 0, note: "" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    if (!form.name.trim() || !form.amount) { toast.error("Name and amount are required"); return; }
    try {
      await api.post("/grants", form);
      toast.success("Grant created");
      setShow(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const allocate = async () => {
    if (!openId) return;
    if (!allocForm.amount) { toast.error("Enter an amount"); return; }
    try {
      await api.post(`/grants/${openId}/allocations`, {
        student_id: allocForm.student_id || null,
        amount: Number(allocForm.amount),
        note: allocForm.note,
      });
      toast.success("Allocation recorded");
      openDetail(openId); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const balance = (g) => Math.max(0, (g.amount || 0) - (g.spent_amount || 0));
  const studentName = (id) => students.find(s => s.id === id)?.name || id;

  const toggleStudent = (id) => setForm(f => ({
    ...f, covered_student_ids: f.covered_student_ids.includes(id)
      ? f.covered_student_ids.filter(x => x !== id) : [...f.covered_student_ids, id],
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1.5">Grants &amp; funding</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Grants &amp; subsidised funding</h1>
          <p className="text-sm text-muted-foreground mt-1">Track government schemes (Samagra Shiksha etc.), CSR, foundation and individual donor contributions.</p>
        </div>
        <Button size="lg" className="gap-1.5 w-full sm:w-auto" onClick={() => { setForm(emptyForm); setShow(true); }} data-testid="new-grant-btn">
          <Plus size={16} weight="bold" /> New grant
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base font-display tracking-tight">All grants ({grants.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {grants.length === 0 ? (
            <div className="px-5 py-14 text-center text-sm text-muted-foreground">
              <HandCoins size={40} weight="duotone" className="mx-auto text-muted-foreground/60 mb-3" />
              No grants yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {grants.map(g => {
                const meta = SOURCE_META[g.source_type];
                const Icon = meta?.icon || User;
                const bal = balance(g);
                const spent = (g.spent_amount || 0);
                const pct = g.amount > 0 ? Math.min(100, (spent / g.amount) * 100) : 0;
                return (
                  <button key={g.id} onClick={() => openDetail(g.id)}
                    className="w-full px-5 py-3 flex items-center gap-4 hover:bg-muted/40 text-left"
                    data-testid={`grant-row-${g.id}`}>
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
                      <Icon size={18} weight="duotone" className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        {g.name}
                        <Badge className={`text-[10px] border ${meta?.color || ""}`}>{meta?.label}</Badge>
                        <Badge variant="outline" className="text-[10px]">{g.allocation_mode === "pool" ? "Pool" : "Specific"}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{g.donor}{g.reference_no && ` · ${g.reference_no}`}</div>
                      <div className="mt-1.5 h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-display text-lg font-bold tabular-nums">{fmtMoney(bal)}</div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">of {fmtMoney(g.amount)}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display tracking-tight">New grant</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2"><Label>Grant name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Samagra Shiksha 2026" data-testid="grant-name" /></div>
              <div className="space-y-1.5"><Label>Donor / issuer</Label><Input value={form.donor} onChange={e => setForm({ ...form, donor: e.target.value })} placeholder="e.g. Karnataka State Govt" /></div>
              <div className="space-y-1.5"><Label>Reference / MoU no</Label><Input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} className="font-mono" /></div>
              <div className="space-y-1.5">
                <Label>Source type</Label>
                <Select value={form.source_type} onValueChange={v => setForm({ ...form, source_type: v })}>
                  <SelectTrigger data-testid="grant-source"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    {Object.entries(SOURCE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Total amount</Label><Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: Number(e.target.value) })} data-testid="grant-amount" /></div>
              <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Allocation mode</Label>
                <Select value={form.allocation_mode} onValueChange={v => setForm({ ...form, allocation_mode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="pool">Pool &mdash; funds general operations; allocate manually</SelectItem>
                    <SelectItem value="specific">Specific &mdash; covers a defined set of students / therapies</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.allocation_mode === "specific" && (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Covered students <span className="text-[10px] text-muted-foreground">(optional; leave empty for &ldquo;any student&rdquo;)</span></Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-border rounded-md">
                    {students.map(s => (
                      <label key={s.id} className={`flex items-center gap-2 p-2 border rounded cursor-pointer text-sm ${form.covered_student_ids.includes(s.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                        <input type="checkbox" checked={form.covered_student_ids.includes(s.id)} onChange={() => toggleStudent(s.id)} />
                        {studentLabel(s)}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full text-sm rounded-md border border-border p-3 bg-background focus:outline-none focus:ring-1 focus:ring-primary" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancel</Button>
            <Button onClick={create} data-testid="grant-save">Create grant</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!openId} onOpenChange={(o) => { if (!o) { setOpenId(null); setDetail(null); } }}>
        <DialogContent className="bg-card max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight">{detail.name}</DialogTitle>
                <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-1">
                  <Badge className={`text-[10px] border ${SOURCE_META[detail.source_type]?.color || ""}`}>{SOURCE_META[detail.source_type]?.label}</Badge>
                  <Badge variant="outline" className="text-[10px]">{detail.allocation_mode === "pool" ? "Pool" : "Specific"}</Badge>
                  {detail.donor && <span>· {detail.donor}</span>}
                </div>
              </DialogHeader>
              <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-2">
                <div className="p-3 rounded-md border border-border"><div className="overline">Total</div><div className="font-display text-2xl font-bold tabular-nums mt-1">{fmtMoney(detail.amount)}</div></div>
                <div className="p-3 rounded-md border border-border"><div className="overline">Spent</div><div className="font-display text-2xl font-bold tabular-nums mt-1">{fmtMoney(detail.spent_amount || 0)}</div></div>
                <div className="p-3 rounded-md border border-emerald-200 bg-emerald-50"><div className="overline">Balance</div><div className="font-display text-2xl font-bold tabular-nums mt-1 text-emerald-800">{fmtMoney(balance(detail))}</div></div>
              </div>

              <div className="mt-5 p-4 rounded-md border border-border">
                <div className="text-sm font-medium mb-3">Record allocation</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs">Student <span className="text-muted-foreground">(optional)</span></Label>
                    <Select value={allocForm.student_id || "none"} onValueChange={v => setAllocForm(a => ({ ...a, student_id: v === "none" ? "" : v }))}>
                      <SelectTrigger data-testid="alloc-student"><SelectValue placeholder="Pick student" /></SelectTrigger>
                      <SelectContent className="bg-card">
                        <SelectItem value="none">&mdash; No specific student &mdash;</SelectItem>
                        {students.map(s => <SelectItem key={s.id} value={s.id}>{studentLabel(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Amount</Label><Input type="number" value={allocForm.amount} onChange={e => setAllocForm(a => ({ ...a, amount: Number(e.target.value) }))} data-testid="alloc-amount" /></div>
                  <Button onClick={allocate} data-testid="alloc-submit">Allocate</Button>
                  <div className="space-y-1.5 col-span-2 sm:col-span-4"><Label className="text-xs">Note</Label><Input value={allocForm.note} onChange={e => setAllocForm(a => ({ ...a, note: e.target.value }))} /></div>
                </div>
              </div>

              <div className="mt-5">
                <div className="overline mb-2">Allocation history ({(detail.allocations || []).length})</div>
                {(detail.allocations || []).length === 0 ? (
                  <div className="text-sm text-muted-foreground italic">No allocations yet.</div>
                ) : (
                  <div className="divide-y divide-border border border-border rounded-md">
                    {detail.allocations.map(a => (
                      <div key={a.id} className="px-3 py-2 flex items-center gap-3 text-sm">
                        <div className="font-mono font-bold text-primary tabular-nums w-24 text-right">− {fmtMoney(a.amount)}</div>
                        <div className="flex-1">
                          <div className="text-sm">{a.student_id ? studentName(a.student_id) : <span className="text-muted-foreground italic">General pool</span>}</div>
                          <div className="text-[11px] text-muted-foreground">{(a.at || "").replace("T", " ").slice(0, 16)} · {a.by_name}{a.note && ` · ${a.note}`}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
